import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AccordionRow from '@/components/common/accordion/AccordionRow';
import {
  ActionAccordionAdditionalColumn,
  ActionAccordionBulkActionContext,
  ActionAccordionHandlers,
  ActionAccordionProps,
  ActionAccordionRowData,
} from '@/types/model/actionAccordion';
import {
  collapseAllRows,
  expandAllMaterializedRows,
  findRowById,
  findRowPathById,
  getSelectedRows,
  setRowSelection,
  toggleRowExpanded,
} from '@/services/util/AccordionUtils';

const buildRowActionKey = (rowId: number, actionId: string): string => `${rowId}::${actionId}`;

const ActionAccordion: React.FC<ActionAccordionProps> = ({
  rows,
  bulkActions = [],
  expandMode = 'multi',
  selectionMode = 'independent',
  onRowsChange,
  onSelectionChange,
  onHandlersReady,
  onRowExpand,
  className,
  style,
}) => {
  const [localRows, setLocalRows] = useState<ActionAccordionRowData[]>(rows);
  const [rowActionRunning, setRowActionRunning] = useState<Record<string, boolean>>({});
  const [bulkActionRunning, setBulkActionRunning] = useState<Record<string, boolean>>({});
  const [isSmallScreen, setIsSmallScreen] = useState<boolean>(window.innerWidth <= 768);

  useEffect(() => {
    setLocalRows(rows);
  }, [rows]);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const selectedRows = useMemo(() => getSelectedRows(localRows), [localRows]);

  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [onSelectionChange, selectedRows]);

  const commitRows = useCallback((nextRows: ActionAccordionRowData[]) => {
    setLocalRows(nextRows);
    onRowsChange?.(nextRows);
  }, [onRowsChange]);

  const handleToggleExpanded = useCallback((row: ActionAccordionRowData) => {
    const nextRows = toggleRowExpanded(localRows, row.id, expandMode);
    commitRows(nextRows);

    const expandedRow = findRowById(nextRows, row.id);
    if (expandedRow?.isExpanded) {
      void onRowExpand?.(expandedRow);
    }
  }, [commitRows, expandMode, localRows, onRowExpand]);

  const handleToggleSelected = useCallback((row: ActionAccordionRowData, checked: boolean) => {
    const nextRows = setRowSelection(localRows, row.id, checked, selectionMode);
    commitRows(nextRows);
  }, [commitRows, localRows, selectionMode]);

  const runRowAction = useCallback(async (
    row: ActionAccordionRowData,
    pathIds: number[],
    actionColumn: ActionAccordionAdditionalColumn
  ) => {
    if (actionColumn.kind !== 'action') {
      return;
    }

    const runningKey = buildRowActionKey(row.id, actionColumn.id);
    const selected = getSelectedRows(localRows);

    setRowActionRunning((prev) => ({ ...prev, [runningKey]: true }));
    try {
      await actionColumn.onAction({
        row,
        rowId: row.id,
        rowType: row.type,
        pathIds,
        selectedRows: selected,
      });
    } finally {
      setRowActionRunning((prev) => {
        const next = { ...prev };
        delete next[runningKey];
        return next;
      });
    }
  }, [localRows]);

  const runBulkAction = useCallback(async (actionId: string) => {
    const action = bulkActions.find((candidate) => candidate.id === actionId);
    if (!action) {
      return;
    }

    const context: ActionAccordionBulkActionContext = {
      selectedRows: getSelectedRows(localRows),
    };

    if (action.isVisible && !action.isVisible(context)) {
      return;
    }

    if (action.isDisabled && action.isDisabled(context)) {
      return;
    }

    setBulkActionRunning((prev) => ({ ...prev, [actionId]: true }));
    try {
      await action.onAction(context);
    } finally {
      setBulkActionRunning((prev) => {
        const next = { ...prev };
        delete next[actionId];
        return next;
      });
    }
  }, [bulkActions, localRows]);

  const expandAll = useCallback(() => {
    const nextRows = expandAllMaterializedRows(localRows);
    commitRows(nextRows);
  }, [commitRows, localRows]);

  const collapseAll = useCallback(() => {
    const nextRows = collapseAllRows(localRows);
    commitRows(nextRows);
  }, [commitRows, localRows]);

  const isBulkActionRunning = useCallback((actionId: string) => {
    return Boolean(bulkActionRunning[actionId]);
  }, [bulkActionRunning]);

  const handlers = useMemo<ActionAccordionHandlers>(() => ({
    expandAll,
    collapseAll,
    getSelectedRows: () => getSelectedRows(localRows),
    runBulkAction,
    isBulkActionRunning,
  }), [collapseAll, expandAll, isBulkActionRunning, localRows, runBulkAction]);

  useEffect(() => {
    onHandlersReady?.(handlers);
  }, [handlers, onHandlersReady]);

  const isRowActionRunning = useCallback((rowId: number, actionColumnId: string) => {
    return Boolean(rowActionRunning[buildRowActionKey(rowId, actionColumnId)]);
  }, [rowActionRunning]);

  return (
    <div className={className} style={{ width: '100%', ...style }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {localRows.map((row) => {
          const path = findRowPathById(localRows, row.id) ?? [row.id];

          return (
            <AccordionRow
              key={row.id}
              row={row}
              depth={0}
              pathIds={path}
              selectedRows={selectedRows}
              isSmallScreen={isSmallScreen}
              onToggleExpanded={handleToggleExpanded}
              onToggleSelected={handleToggleSelected}
              onRunRowAction={runRowAction}
              isRowActionRunning={isRowActionRunning}
            />
          );
        })}
      </div>
    </div>
  );
};

export default ActionAccordion;
